import { Text } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import Stack from '@mui/material/Stack'
import { useEffect, useState } from 'react'

import ShippingMethodPicker from 'views/CalculateEmissions/RFQ/components/ShippingMethodPicker'
import TransportModeGroupSelect from 'views/CalculateEmissions/RFQ/components/TransportModeGroupSelect'
import TransportModeParameters from 'views/CalculateEmissions/RFQ/components/TransportModeParameters'
import { useTransportMode } from 'views/CalculateEmissions/RFQ/hooks/useTransportMode'
import {
    simpleTransportModes,
    TransportMode,
    transportModeArray,
    TransportModeGroup,
    transportModePerGroupArray,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import {
    LegAutoFillWithUpdate,
    OptionalTransportModeMapping,
    RFQTemplateState,
} from 'views/CalculateEmissions/RFQ/utils/types'

export type TransportModeProps = {
    state: RFQTemplateState
    transportModeMapping: OptionalTransportModeMapping
    onUpdate: (mapping: OptionalTransportModeMapping) => void
    autoFill: LegAutoFillWithUpdate['transportMode']
    error?: string
    modeMappingError?: string
    selectedGroup?: TransportModeGroup | 'Select a column'
}

export type TransportModeListOption = {
    group: string
    label: string
    value: TransportMode | 'Select a column'
}

function transportModeGroupToGroup(group: TransportModeGroup): TransportModeListOption['group'] {
    const mapping: Record<TransportModeGroup, TransportModeListOption['group']> = {
        Sea: 'ship',
        Air: 'plane',
        Road: 'truck',
        Rail: 'train',
        'Inland waterways': 'ship',
    }
    return mapping[group]
}

const optionsGroup: Record<
    TransportMode,
    { group: TransportModeListOption['group']; order: number }
> = Object.entries(transportModePerGroupArray).reduce(
    (acc, [group, modes]) => {
        modes.forEach((mode, index) => {
            // Force simple methods to always appear at the end of the list
            const orderOffset = (simpleTransportModes as string[]).includes(mode) ? 1000 : 0
            acc[mode] = {
                group: transportModeGroupToGroup(group as TransportModeGroup),
                order: orderOffset + index,
            }
        })
        return acc
    },
    {} as Record<TransportMode, { group: TransportModeListOption['group']; order: number }>,
)

const options: TransportModeListOption[] = [
    ...transportModeArray
        .map((mode) => ({
            label: mode,
            value: mode,
            group: optionsGroup[mode].group,
        }))
        .sort((a, b) => optionsGroup[a.value].order - optionsGroup[b.value].order),
]

// eslint-disable-next-line complexity
const TransportModePicker = ({
    state,
    transportModeMapping,
    onUpdate,
    error,
    modeMappingError,
    autoFill,
}: TransportModeProps) => {
    const [selectedGroup, setSelectedGroup] = useState<
        TransportModeGroup | undefined | 'Select a column'
    >(undefined)

    useEffect(() => {
        // If the transportModeMapping is removed, keep the group selection since changing
        // group selections triggers deletion of transportModeMapping. We only want to
        // remove current group selection when changing between legs (seen below)
        if (transportModeMapping === undefined) {
            return
        }
        if (transportModeMapping.mode?.source === 'constant') {
            const currentMode = transportModeMapping.mode.value
            for (const [group, modes] of Object.entries(transportModePerGroupArray)) {
                if (modes.includes(currentMode)) {
                    setSelectedGroup(group as keyof typeof transportModePerGroupArray)
                    break
                }
            }
        } else if (transportModeMapping.mode?.source === 'column') {
            setSelectedGroup('Select a column')
        }
    }, [transportModeMapping])

    useEffect(() => {
        setSelectedGroup(undefined)
    }, [state.currentLeg])

    const { handleTransportModeChange, possibleTransportModeOptions } = useTransportMode(
        state,
        transportModeMapping,
        onUpdate,
        autoFill,
        selectedGroup,
    )

    const selectedTransportMode = transportModeMapping
        ? transportModeMapping.mode?.source === 'constant'
            ? options.find(
                  (o) =>
                      o.value ===
                      (transportModeMapping.mode?.source === 'constant'
                          ? transportModeMapping.mode.value
                          : 'Select a column'),
              )
            : {
                  label: 'Select a column',
                  value: 'Select a column' as const,
                  group: 'column',
              }
        : undefined

    return (
        <Stack
            direction="column"
            sx={{
                width: '100%',
            }}
            spacing={{
                xs: 4,
            }}
        >
            <Text variant={'h6'} sx={{ mb: 4 }}>
                Transport mode
            </Text>
            <Box sx={{ width: '45%' }}>
                <TransportModeGroupSelect
                    transportModeError={error}
                    transportModeSelected={selectedTransportMode?.value}
                    selectedGroup={selectedGroup}
                    onUpdate={(group) => {
                        if (group === 'Select a column' || group === undefined) {
                            handleTransportModeChange(group)
                            setSelectedGroup(undefined)
                        } else {
                            handleTransportModeChange(undefined)
                            setSelectedGroup(group)
                        }
                    }}
                />
                {possibleTransportModeOptions &&
                    typeof possibleTransportModeOptions === 'string' &&
                    possibleTransportModeOptions.length > 0 && (
                        <ShippingMethodPicker
                            error={error}
                            selectedGroup={selectedGroup}
                            items={options.filter((o) =>
                                possibleTransportModeOptions.split('|').includes(o.value),
                            )}
                            onChange={(selected) => handleTransportModeChange(selected)}
                            value={selectedTransportMode as TransportModeListOption}
                        />
                    )}
            </Box>
            <TransportModeParameters
                state={state}
                transportModeMapping={transportModeMapping}
                onUpdate={onUpdate}
                error={error}
                modeMappingError={modeMappingError}
                autoFill={autoFill}
                selectedGroup={selectedGroup}
            />
        </Stack>
    )
}

export default TransportModePicker
